/// <reference path="../../app.d.ts" />
import { of, Observable, defer } from 'rxjs';
import { Router } from '@angular/router';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from './api';
import * as initHelpHero from 'helphero';
import * as _ from 'lodash';
import { map, first, publishReplay, refCount, take, tap } from 'rxjs/operators';
import { CurrentContextProvider } from './currentContextProvider.service';
import { TenantService } from './tenant.service';
import { environment } from '../environments/environment';

@Injectable({ providedIn: 'root' })
export class UserService {
    loggedIn:Observable<any>;
    path: string = '/ws/user';
    cachedCurrentUser: Observable<any>;
    cachedUserMap = {};
    help_hero;

    constructor(private api: ApiService, 
                private currentContextProvider: CurrentContextProvider,
                private router: Router,
                private tenantService: TenantService){}

    private captureUser = res => {
        this.currentContextProvider.currentUser = res.user;
        this.tenantService.getUnauthTenantInfo().pipe(first())
        .subscribe(tenant => {
            if (!tenant.settings.blockIntercom) {
                if (res.user) {
                    this.bootIntercom(res.user);
                    this.bootHelpHero(res.user);
                } else {
                    this.bootHelpHero();
                }
            
            }  
        })
         
        return this.currentContextProvider.currentUser;
    };

    bootIntercom(user) {
        if (window['Intercom'] && !window['Intercom'].isBooted) {
            window['Intercom']('boot', { 
                app_id: environment.intercomAppId, 
                user_id: user.id, 
                email: user.emailAddress,
                first_name: user.firstName,
                last_name: user.lastName,
                tenant_id: user.tenantId,
                tenant_host_name: window.location.hostname
             });
            window['Intercom'].isBooted = true;
        }
    }

    bootHelpHero(user?) {
        if (!this.help_hero) {
            const initter = (initHelpHero as unknown) as Function;
            this.help_hero = initter(environment.helpHeroAppId);
        }

        if (user && this.help_hero) {
            this.help_hero.identify(user.id, {
                email: user.emailAddress,
                first_name: user.firstName,
                last_name: user.lastName,
                tenant_id: user.tenantId,
                tenant_host_name: window.location.hostname
            });   
        } else if (this.help_hero?.anonymous) {
            this.help_hero.anonymous();
        }

        if (window['supportHeroWidget'] != undefined) {
            window['supportHeroWidget'].track({ articleParameters: { tenant: window.location.host.split('.')[0] } });
           }
    }

    helpHero() {
        return this.help_hero;
    }

    isLoggedIn  = ():Observable<any> => this.currentContextProvider.currentUser ? of({ user: this.currentContextProvider.currentUser }) : this.api.get(this.path).pipe(tap(this.captureUser));

    getCurrentUser = (): any => this.currentContextProvider.currentUser;

    fetchCurrentUser(): Observable<any> {
        if (!this.cachedCurrentUser) {
            this.cachedCurrentUser = defer(() => this.refetchUser().pipe(map(res => res.user))).pipe(publishReplay(1, 15000), refCount(), take(1));
        }
        return this.cachedCurrentUser;
    }

    fetchUser(id: string): Observable<Attensa.User> {
        return this.api.get(`/ws/user/${id}`);
    }

    isAdmin = (user) =>  _.find(user.tenantRoles, { roleType: 'TENANT_ADMIN' });

    isCatalogManager(user) {
        return user?.tenantRoleActions?.reduce((acc, roleAction) => acc || roleAction.actions?.includes('MANAGE_CATALOG'), false)
    }

    isCurrentUserAdmin = () => this.isAdmin(this.getCurrentUser());

    isCurrentUserCatalogManager() {
        return this.isCatalogManager(this.getCurrentUser());
    }

    signIn = (params: {username, password, stayLoggedIn?}): Observable<any> => {
        this.loggedIn = null;
        return this.api.post('/ws/auth', this.api.paramify(params)).pipe(tap(this.captureUser));
    };

    signOut = (noRedirection = false) => this.api.get('/ws/auth/logout')
    .pipe(tap( response => {
        this.currentContextProvider.currentUser = null;
        this.loggedIn = null;
        if (!noRedirection) {
            if (response.redirect) {
                window.location.href = response.redirect;
            } else {
                this.router.navigate(['/login']);
            }
        }
    }));

    forgotPassword = (emailAddress: string):Observable<any> => this.api.post('/ws/user/forgotPassword', {email: emailAddress, returnPath: 'client/resetPassword' });

    resetPassword = (token:string, newPassword: string):Observable<any> => this.api.post('/ws/user/resetPassword', {token: token, newPassword: newPassword});

    startSignup = (params, path = 'client/register'):Observable<any> => this.api.post('/ws/user/selfSignup', _.defaults(params, { path }));

    canActivate = (id, invitationCode) => {
        let params = new HttpParams();
        if(id){
            params = params.set('id', id);
        }
        if(invitationCode){
            params = params.set('invitationCode', invitationCode);
        }
        return this.api.get('/ws/auth/status', params);
    };

    issuePublicCode(emailAddress: string) {
        return this.api.post('/ws/ua/user', { emailAddress });
    }

    verifyCode(code) {
        return this.api.get('/ws/auth/public', this.api.paramify({code }));
    }

    activateUser = (user) => this.api.post('/ws/user/activateUser', user).pipe(tap(user => this.currentContextProvider.currentUser = user));

    addNewUsers = (newUsers, options?) => this.api.post('/ws/user/addUsers', newUsers, options);

    addTenantRole = (userId, tenantId, roleType) => this.api.post(`/ws/user/${userId}/tenantRoles`, { tenantId: tenantId, roleType: roleType });

    updateCurrentUser = (user) => this.api.put('/ws/user/currentUser', user).pipe(tap(user => this.currentContextProvider.currentUser = user));

    updateUser = (user) => this.api.put('/ws/user/' + user.id, user);

    deleteUser = (userId) =>  this.api.delete('/ws/user/archive', { params: { userId } });

    removeTenantRole = (userId, tenantId, roleType) => this.api.delete(`/ws/user/${userId}/tenantRoles`, { params: this.api.paramify({ tenantId: tenantId, roleType: roleType })});

    refetchUser = () => this.api.get(this.path).pipe(tap(this.captureUser));

    validatePassword = (password: string): Observable<any> => this.api.post(`/ws/auth/validate`, {password: password});

    userStatus = (userId: string) => this.api.get('/ws/auth/status', this.api.paramify({ userId }));

    searchItems = (params: { search?: string, 
                             publishedWithinDays: number,
                             page: number, 
                             includeFullDescription: boolean, 
                             includeOpenForReadingFalseStreams?: boolean, 
                             includeMarketplaceStreams?: boolean, 
                             sortField?: string, 
                             includeReadItems?: boolean,
                             includeAggregations?: boolean, 
                             createdAfterDateTime?: boolean}) => this.api.get('/ws/user/items', this.api.paramify(params));

    searchUsers = (params) =>  this.api.get('/ws/user/search', this.api.paramify(params));

    getUserByEmail(emailAddress: string, hostname?: string): Observable<any> {
        if (!this.cachedUserMap[emailAddress]) {
            this.cachedUserMap[emailAddress] = defer(() => this.api.get(`/ws/user/getUserByEmailAddress`, this.api.paramify({ emailAddress, hostname}))).pipe(publishReplay(1, 5000), refCount(), take(1)); 
        }
        return this.cachedUserMap[emailAddress];
    }

    uaGetUserByEmail = (emailAddress: string, hostname?: string): Observable<any> => this.api.get(`/ws/ua/user/getUserByEmailAddress`, this.api.paramify({ emailAddress, hostname}));

    uaUnsubscribeFromBriefing = (userId, briefingId): Observable<any> => this.api.delete(`/ws/ua/briefings/${briefingId}/user/${userId}`);

    unsubscribeFromBriefing = (user, briefingId, streamId?) => this.api.delete('/ws/user/' + user.id + '/briefings/' + briefingId);

    subscribeToBriefing = (user, briefingId, streamId?) => this.api.post('/ws/user/' + user.id + '/briefings/' + briefingId, { streamId: streamId });

    sendTopicInvitation = (topicId, topicTitle, invitations) => this.api.post('/ws/user/inviteToTopic', { topicId, topicTitle, invitations });

    sendGroupInvitation = (groupId, groupName, invitations) => this.api.post('/ws/user/inviteToGroup', { groupId, groupName, invitations });

    fetchSubscriptionCustomer(): Observable<any> {
        return this.api.get('/ws/user/subscription/customer');
    }
    
    fetchSubscriptionUpcomingInvoice(): Observable<any> {
        return this.api.get('/ws/user/subscription/upcomingInvoice');
    }
    
    fetchSubscriptionInvoices(): Observable<any> {
        return this.api.get('/ws/user/subscription/invoices');
    }

    makeSubscriptionCardDefault(card): Observable<any> {
        return this.api.post('/ws/user/subscriptionCustomer/defaultCard', card);
    }

    removeSubscriptionCard(cardId): Observable<any> {
        return this.api.delete(`/ws/user/subscription/card/${cardId}`);
    }

    createPaymentMethod(token): Observable<any> {
        return this.api.post(`/ws/user/paymentMethod`, token).pipe(tap(() => this.cachedCurrentUser = undefined));
    }
}
